import type {
  CareNavigationDrilldownRow,
  CareNavigationFilters,
  CareNavigationKpi,
} from "./careNavigationTypes";
import { exportRowsToCsv } from "./dashboardsExport";

export const DASHBOARD_TIMEZONE = "America/New_York";
export const MONTH_OPTION_COUNT = 18;
export const CN_PATIENT_ROW_CAP = 20_000;
export const CARE_NAV_TABLE_MAX_HEIGHT_CLASS = "max-h-[min(36rem,calc(12*2.5rem+2.5rem))]";

export const DEFAULT_DRILLDOWN_METRICS = [
  "encounter_completion_pct",
  "avg_monthly_call_minutes_gt15",
  "needs_assessment_45d_pct",
  "care_plan_updates_pct",
  "care_plan_updates_eligible_pct",
  "attrition_pct",
] as const;

export const SUMMARY_KPI_ORDER = [
  "likely_month_end_completion_pct",
  "encounter_completion_pct",
  "care_plan_updates_pct",
  "avg_monthly_call_minutes_gt15",
  "needs_assessment_45d_pct",
  "annual_reassessment_window_pct",
] as const;

export const CALL_COMPLETION = {
  key: "encounter_completion_pct",
  label: "Call completion",
  tooltip:
    "Non-software-only carespaces aligned at any point in the selected month and assigned to a CN, with a qualifying documented completion and corresponding non-rejected DCMP billing. Includes enrollment, needs/annual assessments, ongoing care, connected patient/caregiver calls, and attended groups with completed notes for low tier.",
};

export function todayIsoInTimezone(timeZone = DASHBOARD_TIMEZONE): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function formatUtcDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function getMonthBounds(
  monthKey: string,
  anchorIsoDate: string,
): { dateStart: string; dateEnd: string } {
  const anchor = new Date(`${anchorIsoDate}T00:00:00.000Z`);
  const [year, month] = String(monthKey || "")
    .split("-")
    .map(Number);
  if (!year || !month || Number.isNaN(anchor.valueOf())) {
    return { dateStart: anchorIsoDate, dateEnd: anchorIsoDate };
  }
  const monthStart = new Date(Date.UTC(year, month - 1, 1));
  const monthEnd = new Date(Date.UTC(year, month, 0));
  const isCurrentMonth = year === anchor.getUTCFullYear() && month - 1 === anchor.getUTCMonth();
  return {
    dateStart: formatUtcDate(monthStart),
    dateEnd: formatUtcDate(isCurrentMonth ? anchor : monthEnd),
  };
}

export function getMonthOptions(count: number, anchorIsoDate: string): string[] {
  const anchor = new Date(`${anchorIsoDate}T00:00:00.000Z`);
  const total = Math.max(1, Math.round(Number(count) || 1));
  if (Number.isNaN(anchor.valueOf())) return [];
  const year = anchor.getUTCFullYear();
  const month = anchor.getUTCMonth();
  const keys: string[] = [];
  for (let i = 0; i < total; i += 1) {
    const date = new Date(Date.UTC(year, month - i, 1));
    keys.push(`${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`);
  }
  return keys;
}

export function formatMonthOptionLabel(monthKey: string, currentMonthKey: string): string {
  const [year, month] = String(monthKey || "")
    .split("-")
    .map(Number);
  if (!year || !month) return monthKey;
  const label = new Date(Date.UTC(year, month - 1, 1)).toLocaleString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
  return monthKey === currentMonthKey ? `${label} (MTD)` : label;
}

export function formatPercent(value: unknown, digits = 0): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

export function asMinutes(value: unknown): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(1)} min`;
}

export function asCount(value: unknown): string {
  return Number(value || 0).toLocaleString();
}

export function sanitizeFilters(raw: CareNavigationFilters = {}): CareNavigationFilters {
  const out: CareNavigationFilters = {};
  if (Array.isArray(raw.clinicIds) && raw.clinicIds.length) out.clinicIds = raw.clinicIds;
  if (Array.isArray(raw.navigatorIds) && raw.navigatorIds.length) {
    out.navigatorIds = raw.navigatorIds;
  }
  if (Array.isArray(raw.groupIds) && raw.groupIds.length) out.groupIds = raw.groupIds;
  return out;
}

export function withoutNavigatorFilter(filters: CareNavigationFilters = {}): CareNavigationFilters {
  const next = sanitizeFilters(filters);
  delete next.navigatorIds;
  return next;
}

export function getFilteredKpis(kpis: CareNavigationKpi[] = []): CareNavigationKpi[] {
  return kpis.filter(
    (kpi) => kpi.key !== "monthly_call_over_15_pct" && kpi.key !== "attrition_pct",
  );
}

export function orderSummaryKpis(kpis: CareNavigationKpi[] = []): CareNavigationKpi[] {
  const priority = new Map(SUMMARY_KPI_ORDER.map((key, index) => [key, index]));
  return [...kpis].sort((left, right) => {
    const leftRank =
      priority.get(left.key as (typeof SUMMARY_KPI_ORDER)[number]) ?? Number.MAX_SAFE_INTEGER;
    const rightRank =
      priority.get(right.key as (typeof SUMMARY_KPI_ORDER)[number]) ?? Number.MAX_SAFE_INTEGER;
    if (leftRank !== rightRank) return leftRank - rightRank;
    return left.key.localeCompare(right.key);
  });
}

function weightedAverage(
  rows: CareNavigationDrilldownRow[],
  metric: string,
  weightKey = "carespaces",
): number {
  const totals = rows.reduce(
    (acc: { value: number; weight: number }, row) => {
      const w = Number(row[weightKey] || 0);
      const m = Number(row[metric] || 0);
      acc.weight += w;
      acc.value += w * m;
      return acc;
    },
    { weight: 0, value: 0 },
  );
  return totals.weight > 0 ? totals.value / totals.weight : 0;
}

export function recomputeFilteredKpis(
  snapshotKpis: CareNavigationKpi[],
  cnRows: CareNavigationDrilldownRow[],
  groupRows: CareNavigationDrilldownRow[],
  // A closed month has no bookings left to land, so the same number stops
  // being a projection and becomes the final figure.
  isCurrentMonth = true,
): CareNavigationKpi[] {
  const carespaces = cnRows.reduce((total, row) => total + Number(row.carespaces || 0), 0);
  const completedMtd = cnRows.reduce(
    (total, row) => total + Number(row.encounters_this_month || 0),
    0,
  );
  // The projection needs appointments and low-tier enrolled groups still to
  // land inside the selected month.
  // future_bookings is next month and beyond, so it must not feed this.
  const remainingThisMonth = cnRows.reduce(
    (total, row) => total + Number(row.remaining_this_month || 0),
    0,
  );
  const likelyMonthEndCompletions = completedMtd + remainingThisMonth;
  const likelyMonthEndCompletionPct = carespaces > 0 ? likelyMonthEndCompletions / carespaces : 0;
  const encounterPct = carespaces > 0 ? completedMtd / carespaces : 0;
  const carePlanPct = weightedAverage(cnRows, "care_plan_updates_pct", "encounters_count");
  const needs45dPct = weightedAverage(cnRows, "needs_assessment_45d_pct");
  const avgCallMinutes = weightedAverage(
    groupRows.length ? groupRows : cnRows,
    "avg_monthly_call_minutes_gt15",
    "monthly_disposition_call_count",
  );

  const byKey = new Map(snapshotKpis.map((kpi) => [kpi.key, kpi]));
  const next: CareNavigationKpi[] = [
    {
      key: "likely_month_end_completion_pct",
      label: isCurrentMonth ? "Likely month-end completion" : "Month-end completion",
      value: likelyMonthEndCompletionPct,
      display: formatPercent(likelyMonthEndCompletionPct),
      denominator: isCurrentMonth
        ? `${likelyMonthEndCompletions.toLocaleString()} of ${carespaces.toLocaleString()} carespaces: ${completedMtd.toLocaleString()} completed MTD + ${remainingThisMonth.toLocaleString()} scheduled later this month`
        : `${completedMtd.toLocaleString()} of ${carespaces.toLocaleString()} carespaces completed`,
      tooltip: isCurrentMonth
        ? "Completed MTD plus aligned carespaces with an active qualifying appointment still to come this month. For low-tier carespaces, active enrollment with expected attendance in a group scheduled later in the current month also qualifies. Excludes completed carespaces and bookings beyond this month."
        : "Aligned carespaces with a DCMP-billed encounter in the selected month. The month is closed, so this is final rather than projected.",
    },
    {
      key: "encounter_completion_pct",
      label: CALL_COMPLETION.label,
      value: encounterPct,
      display: formatPercent(encounterPct, 2),
      denominator: `${completedMtd.toLocaleString()} of ${carespaces.toLocaleString()} eligible carespaces completed and DCMP billed`,
      tooltip: CALL_COMPLETION.tooltip,
    },
    {
      key: "care_plan_updates_pct",
      label: byKey.get("care_plan_updates_pct")?.label || "Care Plan Completion %",
      value: carePlanPct,
      display: formatPercent(carePlanPct),
      denominator: "Completed billed encounters",
    },
    {
      key: "avg_monthly_call_minutes_gt15",
      label: byKey.get("avg_monthly_call_minutes_gt15")?.label || "Monthly Call Length",
      value: avgCallMinutes,
      display: asMinutes(avgCallMinutes),
      denominator: "Monthly-call dispositions with recorded duration",
    },
    {
      key: "needs_assessment_45d_pct",
      label: byKey.get("needs_assessment_45d_pct")?.label || "Needs Assessment %",
      value: needs45dPct,
      display: formatPercent(needs45dPct),
      denominator: "Aligned cohort",
    },
  ];

  const reassessment = byKey.get("annual_reassessment_window_pct");
  if (reassessment) next.push(reassessment);

  return orderSummaryKpis(next);
}

export function getDrilldownTotalRow(
  rows: CareNavigationDrilldownRow[] = [],
): CareNavigationDrilldownRow {
  const source = Array.isArray(rows) ? rows : [];
  const sum = (key: string) => source.reduce((total, row) => total + Number(row[key] || 0), 0);
  const weighted = (valueKey: string, weightKey: string) => {
    const weight = sum(weightKey);
    if (!weight) return null;
    return (
      source.reduce(
        (total, row) => total + Number(row[valueKey] || 0) * Number(row[weightKey] || 0),
        0,
      ) / weight
    );
  };
  const carespaces = sum("carespaces");
  const encounters = sum("encounters_count");
  const currentMonthAligned = sum("current_month_aligned_count");
  const currentMonthBillableCalls = sum("current_month_billable_call_count");
  const currentMonthBillableEmails = sum("current_month_billable_email_count");
  const currentMonthBillableCallEmails = sum("current_month_billable_call_email_count");
  const businessDays = Math.max(0, ...source.map((row) => Number(row.business_day_count || 0)), 0);

  return {
    id: "team-total",
    name: "Team Total",
    carespaces,
    encounters_count: encounters,
    encounter_completion_pct: carespaces ? encounters / carespaces : 0,
    current_month_aligned_count: currentMonthAligned,
    current_month_billable_call_count: currentMonthBillableCalls,
    current_month_billable_email_count: currentMonthBillableEmails,
    current_month_billable_call_email_count: currentMonthBillableCallEmails,
    current_month_billable_call_pct: currentMonthAligned
      ? currentMonthBillableCalls / currentMonthAligned
      : 0,
    current_month_billable_email_pct: currentMonthAligned
      ? currentMonthBillableEmails / currentMonthAligned
      : 0,
    current_month_billable_call_email_pct: currentMonthAligned
      ? currentMonthBillableCallEmails / currentMonthAligned
      : 0,
    encounters_this_month: sum("encounters_this_month"),
    encounters_this_month_pct: carespaces ? sum("encounters_this_month") / carespaces : 0,
    bookings_this_month: sum("bookings_this_month"),
    bookings_this_month_pct: carespaces ? sum("bookings_this_month") / carespaces : 0,
    future_bookings: sum("future_bookings"),
    future_bookings_pct: carespaces ? sum("future_bookings") / carespaces : 0,
    remaining_this_month: sum("remaining_this_month"),
    remaining_this_month_pct: carespaces ? sum("remaining_this_month") / carespaces : 0,
    bookings_future: sum("bookings_future"),
    bookings_future_pct: carespaces ? sum("bookings_future") / carespaces : 0,
    outreach_attempts_total: sum("outreach_attempts_total"),
    text_attempts: sum("text_attempts"),
    voicemail_attempts: sum("voicemail_attempts"),
    email_attempts: sum("email_attempts"),
    monthly_call_completions: sum("monthly_call_completions"),
    monthly_call_count: sum("monthly_call_count"),
    monthly_disposition_call_count: sum("monthly_disposition_call_count"),
    avg_monthly_call_minutes_gt15: weighted(
      "avg_monthly_call_minutes_gt15",
      "monthly_disposition_call_count",
    ),
    monthly_call_over_15_pct: sum("monthly_disposition_call_count")
      ? sum("monthly_call_over_15_count") / sum("monthly_disposition_call_count")
      : null,
    needs_assessment_call_count: sum("needs_assessment_call_count"),
    outbound_call_count: sum("outbound_call_count"),
    outbound_calls_per_active_day: businessDays ? sum("outbound_call_count") / businessDays : null,
    inbound_call_count: sum("inbound_call_count"),
    inbound_calls_per_business_day: businessDays ? sum("inbound_call_count") / businessDays : null,
    care_plan_updated_count: sum("care_plan_updated_count"),
    care_plan_updates_pct: encounters ? sum("care_plan_updated_count") / encounters : 0,
    needs_assessment_count: sum("needs_assessment_count"),
    needs_assessment_45d_pct: carespaces ? sum("needs_assessment_count") / carespaces : 0,
    attrition_count: sum("attrition_count"),
    attrition_pct: carespaces ? sum("attrition_count") / carespaces : 0,
  };
}

export function downloadCsv(filename: string, rows: Array<Record<string, unknown>>): void {
  if (!rows.length) return;
  void exportRowsToCsv(rows, filename);
}
